import { ManualMatchRequest, WeightReportLineDetails } from "@/types";
import { useTranslation } from "react-i18next";
import React from "react";
import Drawer, { DrawerHeader } from "@/components/molecules/Drawer";
import DrawerTabs, { DrawerTab } from "@/components/atoms/DrawerTabs";
import styles from "./styles.module.css";
import WeightReportDetailsTab from "@/components/organisms/WeightReportDrawer/tabs/DetailsTab";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import WeightReportDocumentTab from "@/components/organisms/WeightReportDrawer/tabs/DocumentTab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postMatchWeightReport } from "@/services/WeightReports";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MatchOrderToWeightReportLineFormInput, MatchOrderToWeightReportLineSchema } from "./validationSchema";
import MatchOrderTab from "@/components/organisms/WeightReportDrawer/tabs/MatchOrderTab";

interface WeightReportDrawerProps {
  /** The ID of the weight report line to display details for */
  selectedWeightLine: WeightReportLineDetails | null;
  /** Function to set the selected weight report line ID */
  setSelectedWeightLine: (weightLine: WeightReportLineDetails | null) => void;
}

export const WeightReportDrawer: React.FC<WeightReportDrawerProps> = ({
  selectedWeightLine,
  setSelectedWeightLine,
}: {
  selectedWeightLine: WeightReportLineDetails | null;
  setSelectedWeightLine: (weightLineId: WeightReportLineDetails | null) => void;
}) => {
  if (!selectedWeightLine) return null;

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setValue,
  } = useForm<MatchOrderToWeightReportLineFormInput>({
    resolver: zodResolver(MatchOrderToWeightReportLineSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    values: {
      lineId: selectedWeightLine.id,
      orderId: 0,
    },
  });

  const { mutate: mutateMatchWeightReport, isPending } = useMutation({
    mutationFn: async (manualMatchRequest: ManualMatchRequest) => {
      await postMatchWeightReport(manualMatchRequest);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["weightReportLines"] });
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:weightReports.matchWeightReportSuccess")
        )
      );
      setSelectedWeightLine(null);
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:weightReports.matchWeightReportError"))
      );
    },
  });

  const onSubmit = (data: MatchOrderToWeightReportLineFormInput) => {
    const requestData: ManualMatchRequest = {
      lineId: data.lineId,
      orderId: data.orderId,
    };
    mutateMatchWeightReport(requestData);
  };

  return (
    <Drawer
      isOpen={!!selectedWeightLine}
      onClose={() => setSelectedWeightLine(null)}
      title={<DrawerHeader title={t("weightReports:weightReportDetails")} svgName="document22px" />}
    >
      <DrawerTabs defaultValue="details" className="full flex col between h-100 py-10">
        <DrawerTabs.List>
          <DrawerTabs.Tab value="details">
            <DrawerTab svgName="hamburger" label={t("common:details")} />
          </DrawerTabs.Tab>
          <DrawerTabs.Tab value="matchOrder">
            <DrawerTab svgName="document" label={t("weightReports:matchOrdersTab")} />
          </DrawerTabs.Tab>
          <DrawerTabs.Tab value="attachments">
            <DrawerTab svgName="document" label={t("common:documents")} />
          </DrawerTabs.Tab>
        </DrawerTabs.List>
        <DrawerTabs.Panel value="details" className={styles.tabPanelPadded}>
          <WeightReportDetailsTab selectedWeightLine={selectedWeightLine} />
        </DrawerTabs.Panel>
        <DrawerTabs.Panel value="matchOrder" className={styles.tabPanel}>
          <form id={"match-order"} onSubmit={handleSubmit(onSubmit)} className={styles.tabPanel}>
            <MatchOrderTab control={control} setValue={setValue} />
          </form>
        </DrawerTabs.Panel>
        <DrawerTabs.Panel value="attachments" className={styles.tabPanel}>
          <WeightReportDocumentTab selectedWeightLine={selectedWeightLine} />
        </DrawerTabs.Panel>
        <Group className={styles.tabActionGroup} p={15} gap={16} align="center" justify="flex-end">
          <CTA
            intent="primary"
            size="sm"
            fullWidth={false}
            type="submit"
            form={"match-order"}
            disabled={isPending || !isValid}
            loading={isPending}
          >
            {t("actions:save")}
          </CTA>
          <CTA intent="secondary" size="sm" fullWidth={false} type="button" onClick={() => setSelectedWeightLine(null)}>
            {t("actions:close")}
          </CTA>
        </Group>
      </DrawerTabs>
    </Drawer>
  );
};
export default WeightReportDrawer;
